/**
 * Scope Panel — In Scope / Available scenario tree.
 *
 * Replaces the legacy smoke-harness multi-select dropdown. Hosts:
 *   - Loading state with skeleton rows
 *   - Empty states (no v2-enabled instances; no tagged scenarios)
 *   - Error states (network failure, save failure)
 *   - In Scope tree (always expanded) + Available tree (collapsible)
 *   - Filter input on Available
 *   - Reload affordance
 *   - Collapse-to-narrow-tree toggle
 *
 * Click-to-toggle is optimistic with 200ms debounce: rapid clicks coalesce
 * into a single POST /v2/scope/set with the final set. Failures roll
 * back to the last successful scope.
 *
 * Spec: v2-design/SCOPE_PANEL_v0_spec.md
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ChevronLeft,
  ChevronRight,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { HTTPError } from 'src/support/errors';

import {
  ScopeEnvelope,
  ScopeOptionsInstance,
  listScopeOptions,
  setScope as setScopeApi,
} from '../api';

import {
  buildInScopeKeySet,
  countScenarios,
  projectAvailable,
  projectInScope,
} from './projection';
import { ScopeTree } from './ScopeTree';
import { ScopeTreeScenario, scenarioKey } from './types';

const DEBOUNCE_MS = 200;
const COLLAPSED_WIDTH = 140;

interface ScopePanelProps {
  clientId: number;
  // Per-tab dialogue session UUID. Threaded into listScopeOptions
  // and setScope so backend reads/writes are scoped to this session.
  // Parent forces a fresh mount on session change via key={sessionId},
  // so this prop is effectively constant for the panel's lifetime.
  sessionId: string;
  busy: boolean;
  /**
   * Width when expanded. Owned by parent so the drag handle (which lives
   * between panel and chat) can resize. Collapsed state ignores this
   * and uses a fixed narrow width.
   */
  expandedWidth: number;
  /**
   * Reported back to the parent so it can decide whether to render the
   * wrapper-edge frame on the chat side. Also tells the parent how the
   * panel sees its own state for layout adjustments.
   */
  onExpandedChange?: (expanded: boolean) => void;
  // Called when the panel successfully applies a scope change. Parent
  // updates whatever state needs the new scope (e.g., the chat header
  // scope indicator). The panel owns its own scope state too; parent
  // doesn't need to feed it back.
  onScopeChange?: (envelope: ScopeEnvelope) => void;
}

export const ScopePanel: React.FC<ScopePanelProps> = ({
  clientId,
  sessionId,
  busy,
  expandedWidth,
  onExpandedChange,
  onScopeChange,
}) => {
  // ---- data state ----
  const [options, setOptions] = useState<ScopeOptionsInstance[] | null>(null);
  const [scope, setLocalScope] = useState<ScopeEnvelope>({ entries: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---- UI state ----
  const [expanded, setExpanded] = useState(true);
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);
  // Available section open/closed. Auto-open when scope is empty so
  // a fresh user sees their pickable scenarios immediately; otherwise
  // collapsed by default per spec §4.2 ("biased toward the active set").
  const [availableOpen, setAvailableOpen] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  // ---- debounce + rollback bookkeeping ----
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastConfirmedScope = useRef<ScopeEnvelope>({ entries: [] });

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await listScopeOptions(clientId, sessionId);
      setOptions(resp.instances);
      const fresh = resp.current_scope ?? { entries: [] };
      setLocalScope(fresh);
      lastConfirmedScope.current = fresh;
      // Notify the parent so the chat-side wrapper-edge frame
      // renders on initial load (not only after a user toggle).
      onScopeChange?.(fresh);
    } catch (e) {
      setLoadError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [clientId, sessionId, onScopeChange]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Cancel any pending debounced save on unmount so it doesn't fire
  // against the wrong session after the parent remounts this panel
  // via key={sessionId}. Without this, the timer survives unmount,
  // posts to the backend with the OLD sessionId, and the still-mounted
  // parent's onScopeChange handler flips its tint state back on —
  // visible as a brief blue-band flicker after "New Chat".
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, []);

  // ---- toggle + debounced save ----

  const scheduleSave = useCallback(
    (next: ScopeEnvelope) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(async () => {
        try {
          const resp = await setScopeApi(next, sessionId);
          lastConfirmedScope.current = resp.scope_envelope;
          // Reconcile local state with server-enriched envelope (names hydrated)
          setLocalScope(resp.scope_envelope);
          setSaveError(null);
          setPendingKeys(new Set());
          onScopeChange?.(resp.scope_envelope);
        } catch (e) {
          // Roll back to last confirmed and surface error
          setLocalScope(lastConfirmedScope.current);
          setPendingKeys(new Set());
          setSaveError(formatError(e));
        }
      }, DEBOUNCE_MS);
    },
    [onScopeChange, sessionId]
  );

  const handleToggleScenario = useCallback(
    (sc: ScopeTreeScenario) => {
      const key = scenarioKey(sc.model_instance_id, sc.scenario_id);
      const inScopeKeys = buildInScopeKeySet(scope);
      const isCurrentlyInScope = inScopeKeys.has(key);

      let nextEntries: ScopeEnvelope['entries'];
      if (isCurrentlyInScope) {
        nextEntries = scope.entries.filter(
          (e) =>
            !(
              e.model_instance_id === sc.model_instance_id &&
              e.scenario_id === sc.scenario_id
            )
        );
      } else {
        nextEntries = [
          ...scope.entries,
          {
            model_instance_id: sc.model_instance_id,
            scenario_id: sc.scenario_id,
            tag: sc.tag,
            forecast_model_name: sc.forecast_model_name,
            model_instance_name: sc.model_instance_name,
            scenario_name: sc.scenario_name,
          },
        ];
      }
      const nextScope: ScopeEnvelope = { entries: nextEntries };
      setLocalScope(nextScope);
      setPendingKeys((prev) => {
        const n = new Set(prev);
        n.add(key);
        return n;
      });
      scheduleSave(nextScope);
    },
    [scope, scheduleSave]
  );

  // ---- derived trees ----
  const { inScopeTree, availableTree, totalAvailable } = useMemo(() => {
    if (!options) {
      return { inScopeTree: [], availableTree: [], totalAvailable: 0 };
    }
    const inScopeKeys = buildInScopeKeySet(scope);
    const inScope = projectInScope(options, inScopeKeys);
    const available = projectAvailable(options, inScopeKeys, filterText);
    return {
      inScopeTree: inScope,
      availableTree: available,
      totalAvailable: countScenarios(available),
    };
  }, [options, scope, filterText]);

  const inScopeCount = useMemo(
    () => countScenarios(inScopeTree),
    [inScopeTree]
  );
  const hasAnyOptions = options ? options.length > 0 : false;
  const hasAnyTagged = options
    ? options.some((i) => i.scenarios.length > 0)
    : false;

  // Available tree default-expand: when scope is empty (the user
  // needs to discover what's pickable) OR when the tree is small
  // enough that collapsing adds clicks without saving real estate.
  // "Small" = a single model with a single instance — the
  // single-tenant smoke case today.
  const availableDefaultExpandAll = useMemo(() => {
    if (inScopeCount === 0) return true;
    if (availableTree.length === 1 && availableTree[0].instances.length === 1) {
      return true;
    }
    return false;
  }, [inScopeCount, availableTree]);

  const interactionDisabled = busy || loading;

  return (
    <Box
      sx={{
        width: expanded ? expandedWidth : COLLAPSED_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'grey.200',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        // No transition during expanded width changes (drag is direct).
        // Transition only when toggling collapse/expand.
        transition: expanded ? undefined : 'width 150ms ease',
      }}
    >
      <Stack spacing={1} sx={{ pt: 1, pb: 1, pl: 1, pr: 0 }}>
        {/* Panel header */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pr: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              flex: 1,
              color: 'text.secondary',
            }}
          >
            Scope
          </Typography>
          {expanded && (
            <Tooltip title="Reload" enterDelay={500}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => void loadData()}
                  disabled={loading}
                  sx={{ p: 0.25 }}
                >
                  <RefreshIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={expanded ? 'Collapse' : 'Expand'} enterDelay={500}>
            <IconButton
              size="small"
              onClick={() => setExpanded((e) => !e)}
              sx={{ p: 0.25 }}
            >
              {expanded ? (
                <ChevronLeft sx={{ fontSize: 14 }} />
              ) : (
                <ChevronRight sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Save error banner */}
        {saveError && (
          <Alert
            severity="error"
            sx={{ py: 0, fontSize: 11, mr: 1 }}
            onClose={() => setSaveError(null)}
            action={
              <IconButton
                size="small"
                onClick={() => {
                  setSaveError(null);
                  // Trigger a re-save by re-applying current scope
                  scheduleSave(scope);
                }}
              >
                <RefreshIcon sx={{ fontSize: 14 }} />
              </IconButton>
            }
          >
            Save failed: {saveError}
          </Alert>
        )}

        {/* Load error */}
        {loadError && (
          <Alert
            severity="error"
            sx={{ py: 0, fontSize: 11, mr: 1 }}
            action={
              <IconButton size="small" onClick={() => void loadData()}>
                <RefreshIcon sx={{ fontSize: 14 }} />
              </IconButton>
            }
          >
            Failed to load scope options: {loadError}
          </Alert>
        )}

        {/* Loading skeleton */}
        {loading && !loadError && (
          <Stack spacing={0.5} sx={{ pr: 1 }}>
            <Skeleton variant="text" height={22} />
            <Skeleton variant="text" height={22} sx={{ ml: 1.5 }} />
            <Skeleton variant="text" height={22} sx={{ ml: 3 }} />
            <Skeleton variant="text" height={22} sx={{ ml: 3 }} />
          </Stack>
        )}

        {/* Empty: no v2-enabled instances */}
        {!loading && !loadError && options !== null && !hasAnyOptions && (
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No v2 AI-enabled forecast models available. Contact your
              administrator to enable v2 AI on a model instance.
            </Typography>
          </Box>
        )}

        {/* Empty: instances but no tagged scenarios */}
        {!loading && !loadError && hasAnyOptions && !hasAnyTagged && (
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No tagged scenarios available. Tag scenarios in Scenario Manager
              to make them available to the AI.
            </Typography>
          </Box>
        )}

        {/* In Scope section — wrapped with primary-color bracket
            (top/left/bottom, no right) + faint tint when scope is in
            force. The right edge butts against the panel's right
            border which the chat-side frame continues into. */}
        {!loading && !loadError && hasAnyTagged && (
          <Box
            data-scope-frame-anchor="in-scope"
            sx={{
              ...(inScopeCount > 0 && {
                // Muted primary at 30% alpha to match the chat-side
                // wrapper-edge band — one consistent atmospheric tone
                // across the whole "scope is in force" treatment.
                borderTop: '1px solid',
                borderLeft: '1px solid',
                borderBottom: '1px solid',
                borderColor: (theme) => `${theme.palette.primary.main}4D`, // 30%
                bgcolor: (theme) => `${theme.palette.primary.main}0A`,
                // Inset slightly from the panel's left padding so the
                // frame reads as wrapping content rather than as a
                // section border. The right edge extends flush to the
                // panel's right border (no margin) so the top/bottom
                // rules reach the divider with the chat area.
                ml: -0.5,
                pl: 0.5,
                pr: 1,
                pt: 0.5,
                pb: 0.5,
                // No right border — the panel's right edge handles
                // the visual seam into the chat-side band.
              }),
            }}
          >
            <SectionHeader
              label="In Scope"
              count={inScopeCount}
              countSuffix="scenarios"
            />
            {inScopeCount === 0 ? (
              <Box sx={{ px: 1, py: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11 }}
                >
                  {expanded
                    ? 'No scenarios in scope yet — pick one below.'
                    : 'No scope set.'}
                </Typography>
              </Box>
            ) : (
              <ScopeTree
                tree={inScopeTree}
                mode="in-scope"
                pendingKeys={pendingKeys}
                onToggleScenario={handleToggleScenario}
                disabled={interactionDisabled}
              />
            )}
          </Box>
        )}

        {/* Available section (expanded panel only — collapsed shows In Scope only) */}
        {expanded && !loading && !loadError && hasAnyTagged && (
          <Box sx={{ pr: 1 }}>
            <SectionHeader
              label="Available"
              count={totalAvailable}
              countSuffix="scenarios"
              caret={availableOpen ? 'down' : 'right'}
              onCaretClick={() => setAvailableOpen((o) => !o)}
            />
            {availableOpen && (
              <Stack spacing={0.5}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter scenarios…"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 12,
                      height: 28,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 14 }} />
                      </InputAdornment>
                    ),
                    endAdornment: filterText ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setFilterText('')}
                          sx={{ p: 0.25 }}
                        >
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                {availableTree.length === 0 ? (
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 11 }}
                    >
                      {filterText
                        ? 'No scenarios match this filter.'
                        : 'No other scenarios available.'}
                    </Typography>
                  </Box>
                ) : (
                  <ScopeTree
                    tree={availableTree}
                    mode="available"
                    pendingKeys={pendingKeys}
                    onToggleScenario={handleToggleScenario}
                    disabled={interactionDisabled}
                    filterText={filterText}
                    defaultExpandAll={availableDefaultExpandAll}
                  />
                )}
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
interface SectionHeaderProps {
  label: string;
  count: number;
  countSuffix: string;
  caret?: 'down' | 'right';
  onCaretClick?: () => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  label,
  count,
  countSuffix,
  caret,
  onCaretClick,
}) => (
  <Box
    onClick={onCaretClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 0.5,
      py: 0.5,
      cursor: onCaretClick ? 'pointer' : 'default',
      borderBottom: '1px solid',
      borderColor: 'divider',
      mb: 0.25,
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        flex: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        color: 'text.secondary',
        mr: 0.5,
      }}
    >
      {count} {countSuffix}
    </Typography>
    {caret && (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {caret === 'down' ? '▾' : '▸'}
      </Box>
    )}
  </Box>
);

function formatError(e: unknown): string {
  if (e instanceof HTTPError) {
    const body =
      e.body && typeof e.body === 'object'
        ? JSON.stringify(e.body)
        : String(e.body ?? '');
    return `${e.status} ${e.statusText}${body ? `: ${body}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
